import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, pagosAPI } from '../../services/api';

const Dashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [pagosRecientes, setPagosRecientes] = useState([]);
  const [tasaBCV, setTasaBCV] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [kpisRes, pagosRes, tasaRes] = await Promise.all([
        dashboardAPI.getKPIs(),
        dashboardAPI.getPagosRecientes(5),
        pagosAPI.getTasaBCV()
      ]);
      setKpis(kpisRes.data);
      setPagosRecientes(pagosRes.data);
      setTasaBCV(tasaRes.data);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lsfc-blue-600"></div>
      </div>
    );
  }

  const statusLabels = {
    regular: 'Regulares',
    media_beca: 'Media Beca',
    becado: 'Becados'
  };

  const statusColors = {
    regular: 'bg-green-500',
    media_beca: 'bg-yellow-500',
    becado: 'bg-blue-500'
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Dashboard</h1>
        <div className="text-sm text-gray-500">
          Tasa BCV: <span className="font-bold text-lsfc-blue-600">{tasaBCV?.tasa?.toFixed(2)} Bs/$</span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {/* Total Jugadores */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-gray-500 text-xs sm:text-sm">Total Jugadores</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-800">{kpis?.jugadores?.total || 0}</p>
            </div>
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-lsfc-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-2xl">⚽</span>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1 sm:gap-2">
            {Object.entries(kpis?.jugadores?.porStatus || {}).map(([status, cantidad]) => (
              <span key={status} className={`px-2 py-0.5 sm:py-1 ${statusColors[status]} text-white text-[10px] sm:text-xs rounded-full`}>
                {cantidad} {statusLabels[status]}
              </span>
            ))}
          </div>
        </div>

        {/* Ingresos del Mes */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-gray-500 text-xs sm:text-sm">Ingresos del Mes</p>
              <p className="text-xl sm:text-3xl font-bold text-green-600">${kpis?.ingresosMes?.totalUSD?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-2xl">💰</span>
            </div>
          </div>
          <p className="mt-2 text-xs sm:text-sm text-gray-500">
            {kpis?.ingresosMes?.cantidadPagos || 0} pagos confirmados
          </p>
        </div>

        {/* Solicitudes Pendientes */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-gray-500 text-xs sm:text-sm">Solicitudes Pendientes</p>
              <p className="text-2xl sm:text-3xl font-bold text-yellow-600">{kpis?.solicitudesPendientes || 0}</p>
            </div>
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-2xl">📋</span>
            </div>
          </div>
          <Link to="/admin/solicitudes" className="mt-2 text-xs sm:text-sm text-lsfc-blue-600 hover:underline block">
            Ver solicitudes →
          </Link>
        </div>

        {/* Pagos por Confirmar */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-gray-500 text-xs sm:text-sm">Pagos por Confirmar</p>
              <p className="text-2xl sm:text-3xl font-bold text-orange-600">{kpis?.pagosPendientes || 0}</p>
            </div>
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg sm:text-2xl">⏳</span>
            </div>
          </div>
          <Link to="/admin/pagos?estado=pendiente" className="mt-2 text-xs sm:text-sm text-lsfc-blue-600 hover:underline block">
            Ver pagos →
          </Link>
        </div>
      </div>

      {/* Pagos Recientes */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-800">Pagos Recientes</h2>
          <Link to="/admin/pagos" className="text-xs sm:text-sm text-lsfc-blue-600 hover:underline">
            Ver todos →
          </Link>
        </div>

        {pagosRecientes.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay pagos registrados</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Jugador</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Concepto</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Monto</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Estado</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {pagosRecientes.map((pago) => (
                    <tr key={pago.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-800">
                          {pago.jugador_nombres} {pago.jugador_apellidos}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <span className="capitalize">{pago.concepto}</span>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-green-600">${parseFloat(pago.monto_usd || 0).toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{parseFloat(pago.monto_bs || 0).toFixed(2)} Bs</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          pago.estado === 'confirmado' ? 'bg-green-100 text-green-700' :
                          pago.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {pago.estado}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(pago.fecha_pago).toLocaleDateString('es-VE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {pagosRecientes.map((pago) => (
                <div key={pago.id} className="border rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-medium text-gray-800 text-sm">
                      {pago.jugador_nombres} {pago.jugador_apellidos}
                    </p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ml-2 ${
                      pago.estado === 'confirmado' ? 'bg-green-100 text-green-700' :
                      pago.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {pago.estado}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span className="capitalize">{pago.concepto}</span>
                    <span>{new Date(pago.fecha_pago).toLocaleDateString('es-VE')}</span>
                  </div>
                  <div className="mt-1">
                    <span className="font-bold text-green-600 text-sm">${parseFloat(pago.monto_usd || 0).toFixed(2)}</span>
                    <span className="text-xs text-gray-400 ml-2">{parseFloat(pago.monto_bs || 0).toFixed(2)} Bs</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Accesos Rápidos */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
        <Link to="/admin/jugadores/nuevo" className="bg-gradient-to-r from-lsfc-blue-500 to-lsfc-blue-600 rounded-xl p-4 sm:p-6 text-white hover:shadow-lg transition-all flex sm:block items-center gap-3">
          <span className="text-2xl sm:text-3xl">➕</span>
          <div>
            <h3 className="text-base sm:text-lg font-bold sm:mt-2">Nuevo Jugador</h3>
            <p className="text-xs sm:text-sm opacity-80">Registrar jugador manualmente</p>
          </div>
        </Link>

        <Link to="/admin/pagos/nuevo" className="bg-gradient-to-r from-lsfc-green-500 to-lsfc-green-600 rounded-xl p-4 sm:p-6 text-white hover:shadow-lg transition-all flex sm:block items-center gap-3">
          <span className="text-2xl sm:text-3xl">💵</span>
          <div>
            <h3 className="text-base sm:text-lg font-bold sm:mt-2">Registrar Pago</h3>
            <p className="text-xs sm:text-sm opacity-80">Agregar nuevo pago</p>
          </div>
        </Link>

        <Link to="/admin/solicitudes" className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-4 sm:p-6 text-white hover:shadow-lg transition-all flex sm:block items-center gap-3">
          <span className="text-2xl sm:text-3xl">📝</span>
          <div>
            <h3 className="text-base sm:text-lg font-bold sm:mt-2">Revisar Solicitudes</h3>
            <p className="text-xs sm:text-sm opacity-80">{kpis?.solicitudesPendientes || 0} pendientes</p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
